import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Briefcase, Building2, Package, ShoppingCart,
  Warehouse, ChevronLeft, ChevronRight,
  LogOut, Zap, FolderKanban, FileText,
} from 'lucide-react';
import './App.css';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Fournisseurs from './pages/Fournisseurs';
import FournisseurDetail from './pages/FournisseurDetail';
import Equipements from './pages/Equipements';
import EquipementDetail from './pages/EquipementDetail';
import Commandes from './pages/Commandes';
import CommandeDetail from './pages/CommandeDetail';
import ChantierDetail from './pages/ChantierDetail';
import Stocks from './pages/Stocks';
import Parametres from './pages/Parametres';
import EspaceClient from './pages/EspaceClient';
import ProjetClient from './pages/ProjetClient';

import { utilisateurConnecte, utilisateurClient, entreprise } from './data/mockData';

const navItemsAdmin = [
  { section: 'Pilotage' },
  { path: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  { section: 'ERP Stock & Tracabilite' },
  { path: '/clients', icon: Briefcase, label: 'Clients (Marches)' },
  { path: '/fournisseurs', icon: Building2, label: 'Fournisseurs' },
  { path: '/equipements', icon: Package, label: 'Equipements' },
  { path: '/commandes', icon: ShoppingCart, label: 'Commandes' },
  { path: '/stocks', icon: Warehouse, label: 'Stocks & Entrepots' },
];

const navItemsClient = [
  { section: 'Espace client' },
  { path: '/espace-client', icon: FolderKanban, label: 'Mes projets' },
  { path: '/espace-client/documents', icon: FileText, label: 'Tous les documents' },
];

const Navigation = ({ isCollapsed, setIsCollapsed, role, user, onLogout }) => {
  const location = useLocation();
  const navItems = role === 'client' ? navItemsClient : navItemsAdmin;

  return (
    <nav className={`navigation ${isCollapsed ? 'collapsed' : ''} ${role === 'client' ? 'nav-client' : ''}`}>
      <div className="nav-header">
        <div className="nav-brand">
          <div className="brand-icon-wrapper">
            <Zap size={20} />
          </div>
          {!isCollapsed && (
            <div>
              <h1>{entreprise.nom}</h1>
              <p>{role === 'client' ? 'Espace client' : entreprise.slogan}</p>
            </div>
          )}
        </div>
        <button className="nav-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="nav-user">
          <div className="user-avatar">
            {user.prenom[0]}{user.nom[0]}
          </div>
          <div className="user-info">
            <p className="user-name">{user.prenom} {user.nom}</p>
            <p className="user-role">
              {user.role}
              {role === 'client' && user.clientNom ? ` - ${user.clientNom}` : ''}
            </p>
          </div>
        </div>
      )}

      <ul className="nav-menu">
        {navItems.map((item, index) => {
          if (item.section) {
            return !isCollapsed && (
              <li key={`s-${index}`} className="nav-section">{item.section}</li>
            );
          }
          const Icon = item.icon;
          const rootPath = role === 'client' ? '/espace-client' : '/';
          const isActive = item.path === rootPath
            ? location.pathname === rootPath
            : location.pathname.startsWith(item.path);
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={`nav-link ${isActive ? 'active' : ''}`}
                title={isCollapsed ? item.label : ''}
              >
                <Icon size={18} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <div className="nav-footer">
        <button className="nav-logout" title="Deconnexion" onClick={onLogout}>
          <LogOut size={16} />
          {!isCollapsed && <span>Deconnexion</span>}
        </button>
      </div>
    </nav>
  );
};

const ProtectedShell = ({ role, user, onLogout, children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  return (
    <div className="app">
      <Navigation
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
        role={role}
        user={user}
        onLogout={onLogout}
      />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-closed' : ''}`}>
        {children}
      </main>
    </div>
  );
};

function App() {
  // role : null = deconnecte, 'admin' ou 'client' = connecte
  const [role, setRole] = useState(null);

  const handleLogin = (selectedRole) => setRole(selectedRole);
  const handleLogout = () => setRole(null);

  return (
    <Router>
      {!role ? (
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        </Routes>
      ) : role === 'admin' ? (
        <ProtectedShell role="admin" user={utilisateurConnecte} onLogout={handleLogout}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/chantiers/:id" element={<ChantierDetail />} />
            <Route path="/fournisseurs" element={<Fournisseurs />} />
            <Route path="/fournisseurs/:id" element={<FournisseurDetail />} />
            <Route path="/equipements" element={<Equipements />} />
            <Route path="/equipements/:id" element={<EquipementDetail />} />
            <Route path="/commandes" element={<Commandes />} />
            <Route path="/commandes/:id" element={<CommandeDetail />} />
            <Route path="/stocks" element={<Stocks />} />
            <Route path="/parametres" element={<Parametres />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ProtectedShell>
      ) : (
        <ProtectedShell role="client" user={utilisateurClient} onLogout={handleLogout}>
          <Routes>
            <Route path="/espace-client" element={<EspaceClient />} />
            <Route path="/espace-client/projets/:id" element={<ProjetClient />} />
            <Route path="/espace-client/documents" element={<EspaceClient defaultTab="documents" />} />
            <Route path="*" element={<Navigate to="/espace-client" replace />} />
          </Routes>
        </ProtectedShell>
      )}
    </Router>
  );
}

export default App;
